/* ============================================================
   G900Pro — shared behaviour for all landing sections.
   Every initialiser is idempotent and scoped to a root element,
   so it re-runs safely on shopify:section:load (theme editor).
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── reveal on scroll ──────────────────────────────── */
  var io = null;
  function reveal(root) {
    var items = root.querySelectorAll('.g900-rv:not(.is-in)');
    if (!items.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
    }
    items.forEach(function (el) { io.observe(el); });
  }

  /* ── tabs: [data-g900-tabs] with [data-tab] / [data-pane] ──
     data-rotate="<seconds>" turns on auto-rotation. The countdown rail
     under each tab is drawn from --p (0 → 1), so the rail has to stay
     honest about the clock: it pauses wherever rotation does — panel
     off-screen, browser tab hidden, pointer or focus resting on the
     section — and resumes from where it stopped instead of restarting.
     The tab row is a single-line scroller at every width; edge fades are
     set from the real scroll offset so only an edge with more content
     behind it is faded.                                                */
  function tabs(root) {
    root.querySelectorAll('[data-g900-tabs]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';

      var btns  = [].slice.call(box.querySelectorAll('[data-tab]'));
      var panes = [].slice.call(box.querySelectorAll('[data-pane]'));
      if (!btns.length) return;
      var rail  = box.querySelector('[data-tab-rail]') || btns[0].parentElement;
      var DUR   = (parseFloat(box.getAttribute('data-rotate')) || 0) * 1000;
      var auto  = DUR > 0 && !reduce && btns.length > 1;
      var FADE  = 36;                                   // px of mask ramp

      var at = 0;
      btns.forEach(function (b, n) { if (b.classList.contains('is-on')) at = n; });

      /* Fade only the side that still has tabs behind it, so a rail
         scrolled to an end doesn't look clipped. */
      function fades() {
        if (!rail) return;
        var max = rail.scrollWidth - rail.clientWidth;
        if (max <= 1) {
          rail.style.setProperty('--fade-l', '0px');
          rail.style.setProperty('--fade-r', '0px');
          return;
        }
        var x = rail.scrollLeft;
        rail.style.setProperty('--fade-l', Math.min(FADE, x) + 'px');
        rail.style.setProperty('--fade-r', Math.min(FADE, max - x) + 'px');
      }

      /* Pull the live tab into view by setting scrollLeft on the rail —
         scrollIntoView would scroll every ancestor and yank the page as
         the rotation advances. Measured from rects: the rail is not the
         offsetParent, so offsetLeft would carry the wrapper padding. */
      function follow(b) {
        if (!rail) return;
        var max = rail.scrollWidth - rail.clientWidth;
        if (max <= 1) return;
        var here = b.getBoundingClientRect().left - rail.getBoundingClientRect().left;
        var want = rail.scrollLeft + here - (rail.clientWidth - b.offsetWidth) / 2;
        rail.scrollTo({
          left: Math.max(0, Math.min(max, want)),
          behavior: reduce ? 'auto' : 'smooth'
        });
      }

      /* ── the clock ── */
      var spent = 0, mark = 0, raf = 0;
      var hover = false;                                // mouse resting on the section
      var kbd   = false;                                // keyboard focus inside it
      var seen  = !('IntersectionObserver' in window);  // section in view

      function live() { return auto && seen && !hover && !kbd && !document.hidden; }
      function stop() { if (raf) cancelAnimationFrame(raf); raf = 0; mark = 0; }
      function start() { if (live() && !raf) { mark = 0; raf = requestAnimationFrame(step); } }
      function step(ts) {
        raf = 0;
        if (!live()) { mark = 0; return; }
        if (!mark) mark = ts;
        spent += ts - mark;
        mark = ts;
        if (spent >= DUR) { show(at + 1); return; }
        btns[at].style.setProperty('--p', (spent / DUR).toFixed(4));
        raf = requestAnimationFrame(step);
      }

      function show(n) {
        at = ((n % btns.length) + btns.length) % btns.length;
        var key = btns[at].getAttribute('data-tab');
        btns.forEach(function (b) {
          var on = b === btns[at];
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          if (!on) b.style.removeProperty('--p');       // let CSS reset the rail
        });
        panes.forEach(function (p) {
          var on = p.getAttribute('data-pane') === key;
          p.classList.toggle('is-on', on);
          /* restart the pane's clip from the top so each selling point
             shows its footage from frame one, and don't leave hidden
             videos decoding in the background */
          var v = p.querySelector('video');
          if (!v) return;
          if (!on) { v.pause(); return; }
          try { v.currentTime = 0; } catch (err) {}
          var go = v.play();
          if (go && go.catch) go.catch(function () {});
        });
        follow(btns[at]);
        stop();
        spent = 0;
        if (auto) { btns[at].style.setProperty('--p', '0'); start(); }
      }

      btns.forEach(function (b, n) {
        b.addEventListener('click', function () { show(n); });
      });

      /* arrow keys, since these carry role="tab" */
      if (rail) {
        rail.addEventListener('keydown', function (e) {
          var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          show(at + d);
          btns[at].focus();
        });

        rail.addEventListener('scroll', fades, { passive: true });
        fades();
        /* late layout — webfonts swapping in, or the theme editor
           revealing the section — changes scrollWidth after init */
        if ('ResizeObserver' in window) {
          var ro = new ResizeObserver(function () { fades(); follow(btns[at]); });
          ro.observe(rail);
        }
        window.addEventListener('resize', fades, { passive: true });
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(fades);
      }

      if (!auto) return;

      /* Hover-pause is bound to the tab row, not the whole box: a cursor
         parked mid-screen while the visitor scrolls would otherwise land
         on the panel and stop the rotation before it ever ran once. */
      rail.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse') { hover = true; stop(); }
      });
      rail.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'mouse') { hover = false; start(); }
      });
      /* Only a KEYBOARD focus holds the clock. A tap focuses the button
         too on mobile, and treating that as "reading" would park the
         rotation for good on the first tap. */
      box.addEventListener('focusin', function (e) {
        var vis = false;
        try { vis = !!(e.target && e.target.matches && e.target.matches(':focus-visible')); } catch (err) {}
        if (vis) { kbd = true; stop(); }
      });
      box.addEventListener('focusout', function () { kbd = false; start(); });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });

      if ('IntersectionObserver' in window) {
        /* threshold 0 with an inset root: a pane taller than the viewport
           can never reach a ratio threshold, so measure "is any of it in
           the middle of the screen" instead. */
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            seen = e.isIntersecting;
            if (seen) start(); else stop();
          });
        }, { threshold: 0, rootMargin: '-10% 0px -10% 0px' }).observe(box);
      }

      btns[at].style.setProperty('--p', '0');
      start();
    });
  }

  /* ── accordion: [data-g900-accordion] with [data-q] ── */
  function accordion(root) {
    root.querySelectorAll('[data-g900-accordion]').forEach(function (list) {
      if (list.dataset.g900Ready) return;
      list.dataset.g900Ready = '1';
      var rows = [].slice.call(list.querySelectorAll('[data-q]'));

      function size(row, open) {
        var body = row.querySelector('[data-a]');
        if (!body || !body.firstElementChild) return;
        body.style.height = open ? body.firstElementChild.offsetHeight + 'px' : '0px';
      }
      rows.forEach(function (row) {
        var btn = row.querySelector('[data-q-btn]');
        size(row, row.classList.contains('is-open'));
        if (btn) {
          btn.addEventListener('click', function () {
            var open = !row.classList.contains('is-open');
            row.classList.toggle('is-open', open);
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
            size(row, open);
          });
        }
      });
      var resize = window.G900 && window.G900._rz;
      if (!resize) {
        window.addEventListener('resize', function () {
          document.querySelectorAll('[data-g900-accordion] [data-q].is-open').forEach(function (r) {
            var body = r.querySelector('[data-a]');
            if (body && body.firstElementChild) body.style.height = body.firstElementChild.offsetHeight + 'px';
          });
        });
      }
    });
  }

  /* ── folded list: [data-g900-more] ─────────────────────────
     A long list that opens to its first N items. The folded tail is
     NOT wrapped in a container — the rows stay direct children of the
     well, so striping, hairlines and a table's ARIA rows are unaffected
     by where the cut falls. What animates is the well's own height:
     from the top of [data-more-cut] to the full list, then released to
     auto so a row re-wrapping at a new width can't strand it at a
     stale pixel height.                                             */
  function more(root) {
    root.querySelectorAll('[data-g900-more]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';

      var well = box.querySelector('[data-more-well]');
      var btn  = box.querySelector('[data-more-btn]');
      var cut  = box.querySelector('[data-more-cut]');
      if (!well || !btn || !cut) return;            // nothing folded away
      var label = btn.querySelector('[data-more-label]');
      var open  = box.classList.contains('is-open');
      var timer = 0;

      /* Height up to the cut row. Measured from rects — the well is not
         the offsetParent — and clipping the well never moves the rows
         below the cut, so this reads the same open or shut. */
      function shut() {
        return Math.max(0, Math.round(
          cut.getBoundingClientRect().top - well.getBoundingClientRect().top
        ));
      }
      function apply(animate) {
        clearTimeout(timer);
        if (!open) { well.style.height = shut() + 'px'; return; }
        if (!animate || reduce) { well.style.height = ''; return; }
        well.style.height = well.scrollHeight + 'px';
        timer = setTimeout(function () { well.style.height = ''; }, 480);
      }

      btn.addEventListener('click', function () {
        /* an open well rests at height:auto, which has nothing to
           transition from — pin it to its measured height first */
        if (open) { well.style.height = well.scrollHeight + 'px'; }
        open = !open;
        box.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        var txt = btn.getAttribute(open ? 'data-less-text' : 'data-more-text');
        if (label && txt) label.textContent = txt;
        if (!open) void well.offsetHeight;          // flush the start value
        apply(true);
      });

      window.addEventListener('resize', function () {
        if (!open) apply(false);
      }, { passive: true });
      /* webfonts swapping in re-wrap the rows above the cut */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { if (!open) apply(false); });
      }

      apply(false);
    });
  }

  /* ── linked hotspots ↔ steps: [data-g900-link] with [data-i] ── */
  function linked(root) {
    root.querySelectorAll('[data-g900-link]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';
      var items = [].slice.call(box.querySelectorAll('[data-i]'));
      function pick(i) {
        items.forEach(function (el) { el.classList.toggle('is-on', el.getAttribute('data-i') === i); });
      }
      items.forEach(function (el) {
        var i = el.getAttribute('data-i');
        el.addEventListener('click', function () { pick(i); });
        el.addEventListener('mouseenter', function () { pick(i); });
        el.addEventListener('focus', function () { pick(i); });
      });
    });
  }

  /* ── install steps: [data-g900-steps] with [data-step] ─────
     One markup, two behaviours, switched at 990px.

     Mobile  each step collapses to its heading and opens on tap,
             one at a time — the whole list has to stay thumb-sized.
     Desktop every body is open and the media column beside it is
             sticky, so the live step follows the scroll position
             instead of a tap. The dots advance as you read.

     Heights are set inline only in accordion mode; desktop clears
     them so the CSS height:auto takes over. That is what lets a
     browser resized across the breakpoint end up in a sane state
     rather than stuck at a measured pixel height.               */
  function steps(root) {
    root.querySelectorAll('[data-g900-steps]').forEach(function (list) {
      if (list.dataset.g900Ready) return;
      list.dataset.g900Ready = '1';

      var rows = [].slice.call(list.querySelectorAll('[data-step]'));
      if (!rows.length) return;
      var wide = window.matchMedia('(min-width: 990px)');
      var link = list.closest('[data-g900-link]') || list;   // hotspots share the index
      var at   = 0;
      rows.forEach(function (r, n) { if (r.classList.contains('is-on')) at = n; });

      function inner(row) {
        var b = row.querySelector('[data-step-a]');
        return b && b.firstElementChild ? b : null;
      }
      function size(row, open) {
        var b = inner(row);
        if (b) b.style.height = open ? b.firstElementChild.offsetHeight + 'px' : '0px';
      }
      function fold(row, open) {
        row.classList.toggle('is-open', open);
        var btn = row.querySelector('[data-step-btn]');
        /* data-a-id is only printed for steps that HAVE a body — a step
           without one is a plain label, not a collapsed disclosure */
        if (btn && btn.getAttribute('data-a-id')) {
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        size(row, open);
      }
      /* Toggling every [data-i] in the box, not just the steps, is what
         keeps the diagram hotspots in step with the list. */
      function pick(n) {
        at = n;
        link.querySelectorAll('[data-i]').forEach(function (el) {
          el.classList.toggle('is-on', el.getAttribute('data-i') === String(n));
        });
      }

      var mode = null;
      function apply() {
        var next = wide.matches ? 'desk' : 'mob';
        if (next === mode) return;
        mode = next;
        rows.forEach(function (r, n) {
          var btn = r.querySelector('[data-step-btn]');
          var aid = btn && btn.getAttribute('data-a-id');
          if (mode === 'desk') {
            /* not a disclosure up here — every body is open and the button
               only moves the live step, so drop the disclosure semantics */
            r.classList.add('is-open');
            var b = inner(r);
            if (b) b.style.height = '';                 // let CSS open it
            if (btn) { btn.removeAttribute('aria-expanded'); btn.removeAttribute('aria-controls'); }
          } else {
            if (btn && aid) btn.setAttribute('aria-controls', aid);
            fold(r, n === at);
          }
        });
        if (mode === 'desk') spy();
      }

      /* Scroll-spy: the live step is the last one whose top has passed the
         reading line, and the line is measured off the pinned media
         ([data-step-anchor]) rather than the viewport — a fixed viewport
         line lights the wrong step at rest, because how much of the
         section is on screen when you arrive depends on the window
         height. A third of the way down the media reads as "beside it".  */
      var anchor = link.querySelector('[data-step-anchor]');
      function line() {
        if (anchor) {
          var r = anchor.getBoundingClientRect();
          if (r.height) return r.top + r.height * 0.35;
        }
        return window.innerHeight * 0.35;
      }
      function spy() {
        if (!wide.matches) return;
        var y = line(), cur = 0;
        rows.forEach(function (r, n) {
          if (r.getBoundingClientRect().top <= y) cur = n;
        });
        if (cur !== at) pick(cur);
      }

      rows.forEach(function (row, n) {
        var btn = row.querySelector('[data-step-btn]');
        if (!btn) return;
        btn.addEventListener('click', function () {
          if (wide.matches) { pick(n); return; }
          var open = !row.classList.contains('is-open');
          rows.forEach(function (r, k) { fold(r, k === n && open); });
          if (open) pick(n);
        });
      });

      window.addEventListener('scroll', spy, { passive: true });
      window.addEventListener('resize', function () {
        apply();
        /* an open body re-wraps at a new width, so re-measure it */
        if (!wide.matches) rows.forEach(function (r) {
          if (r.classList.contains('is-open')) size(r, true);
        });
      }, { passive: true });
      /* webfonts swapping in change the body height after init */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          if (!wide.matches) rows.forEach(function (r) {
            if (r.classList.contains('is-open')) size(r, true);
          });
        });
      }

      apply();
      spy();
    });
  }

  /* ── before/after slider: [data-g900-compare] ── */
  function compare(root) {
    root.querySelectorAll('[data-g900-compare]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';
      var dragging = false;

      function setX(clientX) {
        var r = box.getBoundingClientRect();
        var pct = Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100));
        box.style.setProperty('--x', pct + '%');
      }
      box.addEventListener('pointerdown', function (e) {
        dragging = true;
        if (box.setPointerCapture) box.setPointerCapture(e.pointerId);
        setX(e.clientX);
      });
      box.addEventListener('pointermove', function (e) { if (dragging) setX(e.clientX); });
      box.addEventListener('pointerup', function () { dragging = false; });
      box.addEventListener('pointercancel', function () { dragging = false; });

      // keyboard support
      box.setAttribute('tabindex', '0');
      box.addEventListener('keydown', function (e) {
        var cur = parseFloat(box.style.getPropertyValue('--x')) || 50;
        if (e.key === 'ArrowLeft')  { box.style.setProperty('--x', Math.max(2, cur - 4) + '%');  e.preventDefault(); }
        if (e.key === 'ArrowRight') { box.style.setProperty('--x', Math.min(98, cur + 4) + '%'); e.preventDefault(); }
      });

      // demo the interaction once, the first time it scrolls into view
      if (reduce || !('IntersectionObserver' in window)) return;
      var done = false;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting || done) return;
          done = true;
          var t0 = null, dur = 2200;
          requestAnimationFrame(function step(ts) {
            if (dragging) return;
            if (t0 === null) t0 = ts;
            var p = Math.min(1, (ts - t0) / dur);
            var k = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
            box.style.setProperty('--x', (50 + Math.sin(k * Math.PI) * 32) + '%');
            if (p < 1) requestAnimationFrame(step);
          });
        });
      }, { threshold: 0.45 }).observe(box);
    });
  }

  /* ── gallery: [data-g900-gallery] ──────────────────────
     Main shot + a horizontal thumb rail, with prev/next arrows.
     Slots are addressed by their data-shot KEY, not by array
     position: the section only prints the slots a merchant filled,
     so keys stay stable while positions do not. That is what lets
     a kit point at "gallery image 3" and still land on it.
     box.g900Show(key) is the public hook the buy block calls.   */
  function gallery(root) {
    root.querySelectorAll('[data-g900-gallery]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';
      var shots  = [].slice.call(box.querySelectorAll('[data-shot]'));
      var thumbs = [].slice.call(box.querySelectorAll('[data-thumb]'));
      var rail   = box.querySelector('[data-thumb-rail]');
      var prev   = box.querySelector('[data-shot-prev]');
      var next   = box.querySelector('[data-shot-next]');
      if (!shots.length) return;

      var keys = shots.map(function (s) { return s.getAttribute('data-shot'); });
      var at   = 0;
      shots.forEach(function (s, i) { if (s.classList.contains('is-on')) at = i; });

      // a single image needs no arrows
      if (shots.length < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
      }

      /* Pull the live thumb into view by setting scrollLeft on the rail —
         scrollIntoView would scroll every ancestor and yank the page.
         Measured from rects, as in tabs(): the rail is not the
         offsetParent, so offsetLeft would carry the wrapper padding. */
      function center(i) {
        if (!rail || !thumbs[i] || !rail.scrollTo) return;
        var max = rail.scrollWidth - rail.clientWidth;
        if (max <= 1) return;
        var t = thumbs[i];
        var here = t.getBoundingClientRect().left - rail.getBoundingClientRect().left;
        var want = rail.scrollLeft + here - (rail.clientWidth - t.offsetWidth) / 2;
        rail.scrollTo({
          left: Math.max(0, Math.min(max, want)),
          behavior: reduce ? 'auto' : 'smooth'
        });
      }
      function go(i, scroll) {
        if (i < 0) i = shots.length - 1;
        if (i >= shots.length) i = 0;
        at = i;
        shots.forEach(function (s, k) { s.classList.toggle('is-on', k === at); });
        thumbs.forEach(function (t, k) {
          t.classList.toggle('is-on', k === at);
          t.setAttribute('aria-current', k === at ? 'true' : 'false');
        });
        if (scroll) center(at);
      }

      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () { go(i, false); });
      });
      if (prev) prev.addEventListener('click', function () { go(at - 1, true); });
      if (next) next.addEventListener('click', function () { go(at + 1, true); });

      /* kit picker → matching image. scroll is opt-out so the buy block can
         line the gallery up with the starting variant on load without a
         thumb rail sliding about before anyone has touched anything. */
      box.g900Show = function (key, scroll) {
        var i = keys.indexOf(String(key));
        if (i > -1 && i !== at) go(i, scroll !== false);
      };
    });
  }

  /* ── options, quantity, cart: [data-g900-buy] ──────────────
     One state, two sets of controls. The buy panel and the sticky bar print
     the same [data-kit] options, the same stepper and the same price block,
     so every lookup here is a LIST and a click on either copy paints both.
     Everything an option needs rides on the element itself, which is what
     lets a chip in the bar and a row in the panel share this code.

     Money: Liquid prints the unit, compare-at and save strings through the
     shop's own money filter — the only honest source for currency — and
     carries the cents alongside, so the running total is the only figure
     this file has to format.                                              */
  function buy(root) {
    root.querySelectorAll('[data-g900-buy]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';

      function all(sel) { return [].slice.call(box.querySelectorAll(sel)); }

      var opts   = all('[data-kit]');
      var gal    = box.querySelector('[data-g900-gallery]');       // option → its photo
      var note   = box.querySelector('[data-cart-note]');
      var live   = !!box.querySelector('[data-variant-input]');    // a real cart form
      var MAX    = parseInt(box.getAttribute('data-max'), 10) || 10;
      var SOLD   = box.getAttribute('data-sold-label')  || 'Sold out';
      var ADDED  = box.getAttribute('data-added-label') || 'Added';
      var OOPS   = box.getAttribute('data-error-label') || 'Try again';
      var ajax   = box.getAttribute('data-ajax') === '1';
      var addUrl = (box.getAttribute('data-cart-add') || '/cart/add').replace(/\.js$/, '') + '.js';
      var cur    = opts.filter(function (k) { return k.classList.contains('is-on'); })[0] || opts[0] || null;
      var qty    = 1;

      // shop.money_format, supplied by the section via data-money.
      // Deliberately never written as a literal in this file: a bare Liquid
      // amount token here reads as a Liquid object to theme-check, and would
      // actually be rendered away if this ever became a .js.liquid file.
      // Tags are stripped: a currency switcher installs a <span class="money">
      // wrapper into the format, and this lands in textContent, where markup
      // would read out as markup.
      var money = (box.getAttribute('data-money') || '').replace(/<[^>]*>/g, '');
      var TOKEN = new RegExp('\\{\\{\\s*([a-z_0-9]+)\\s*\\}\\}');

      /* The format names its own shape: the no_decimals variants drop the
         cents, the comma variants read 1.234,56. Getting that wrong shows
         up as a total that disagrees with the price printed above it. */
      function fmt(cents) {
        var hit  = TOKEN.exec(money);
        var kind = hit ? hit[1] : 'amount';
        var n    = cents / 100;
        var s    = kind.indexOf('no_decimals') > -1 ? String(Math.round(n)) : n.toFixed(2);
        var swap = kind.indexOf('with_comma_separator') > -1;
        var bits = s.split('.');
        bits[0] = bits[0].replace(/\B(?=(\d{3})+(?!\d))/g, swap ? '.' : ',');
        s = bits.join(swap ? ',' : '.');
        if (!hit) return money ? money + s : '$' + s;
        return money.replace(hit[0], s);
      }

      function fill(sel, value, hide) {
        all(sel).forEach(function (el) {
          el.textContent = value;
          if (hide) el.hidden = !value;
        });
      }

      function paint() {
        if (!cur) return;
        var cents = parseFloat(cur.getAttribute('data-cents')) || 0;
        var sold  = cur.getAttribute('data-sold') === '1';
        var stock = parseInt(cur.getAttribute('data-stock'), 10);
        var cap   = isNaN(stock) ? MAX : Math.min(MAX, stock);
        var idx   = cur.getAttribute('data-idx');

        qty = Math.max(1, Math.min(qty, cap));

        opts.forEach(function (k) {
          var on = k.getAttribute('data-idx') === idx;
          k.classList.toggle('is-on', on);
          k.setAttribute('aria-checked', on ? 'true' : 'false');
          k.tabIndex = on ? 0 : -1;          // one tab stop per group, as a radio set has
        });

        fill('[data-price-now]', cur.getAttribute('data-price-str') || fmt(cents));
        fill('[data-price-was]', cur.getAttribute('data-was-str') || '', true);
        fill('[data-price-off]', cur.getAttribute('data-off') || '', true);
        fill('[data-kit-name]',  cur.getAttribute('data-name') || '');
        fill('[data-qty]', String(qty));
        fill('[data-total]', fmt(cents * qty));
        all('[data-total-wrap]').forEach(function (el) { el.hidden = qty < 2; });
        all('[data-qty-input]').forEach(function (el) { el.value = qty; });

        var v = cur.getAttribute('data-variant');
        if (v) all('[data-variant-input]').forEach(function (el) { el.value = v; });

        all('[data-minus]').forEach(function (b) { b.disabled = qty <= 1; });
        all('[data-plus]').forEach(function (b) { b.disabled = qty >= cap; });

        /* A variant nobody can buy says so on the button, instead of letting
           the click travel to a cart that will only refuse it. */
        all('[data-add],[data-checkout]').forEach(function (b) {
          if (!b.dataset.label) b.dataset.label = b.textContent.trim();
          if (b.dataset.flash) return;                     // mid-confirmation
          if (b.hasAttribute('data-add')) b.textContent = sold ? SOLD : b.dataset.label;
          if (b.tagName === 'BUTTON') b.disabled = sold;
          else b.classList.toggle('is-out', sold);
        });
      }

      function pick(el) {
        if (!el || el === cur) return;
        cur = el;
        qty = 1;                                           // a new option, a fresh count
        paint();
        var key = el.getAttribute('data-shot-index');
        if (gal && gal.g900Show && key) gal.g900Show(key);
      }

      /* Delegated, because both copies of every control live under this box
         and the sticky bar's set is printed twice over. */
      box.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var k = t.closest('[data-kit]');
        if (k && opts.indexOf(k) > -1) { pick(k); return; }
        if (t.closest('[data-minus]')) { qty--; paint(); return; }
        if (t.closest('[data-plus]'))  { qty++; paint(); return; }
      });

      /* Arrow keys walk each group the way a native radio set would — these
         options are buttons, so nothing gives us that for free. */
      all('[role="radiogroup"]').forEach(function (grp) {
        grp.addEventListener('keydown', function (e) {
          var step = 0;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') step = 1;
          if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   step = -1;
          if (!step) return;
          var list = [].slice.call(grp.querySelectorAll('[data-kit]:not([disabled])'));
          if (!list.length) return;
          var at = list.indexOf(document.activeElement);
          if (at < 0) at = list.indexOf(cur);
          var next = list[(at + step + list.length) % list.length];
          if (!next) return;
          e.preventDefault();
          next.focus();
          pick(next);
        });
      });

      function flash(btn, text) {
        if (btn.dataset.flash) return;
        btn.dataset.flash = '1';
        var was = btn.textContent;
        btn.textContent = text;
        setTimeout(function () {
          btn.textContent = was;
          delete btn.dataset.flash;
          paint();
        }, 1800);
      }

      /* Themes disagree on the name of "your cart chrome is stale", so fire
         the common ones and let each theme ignore the rest. */
      function ping() {
        if (typeof CustomEvent !== 'function') return;
        ['cart:refresh', 'cart:updated', 'cart:build'].forEach(function (name) {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
        });
      }

      /* Adding through /cart/add.js keeps the shopper where they are, and it
         is also how Checkout gets a dependable exit: add first, then move,
         rather than trusting a theme not to swallow the form's own submit.
         A refusal from the cart (stock, usually) is reported; only a dead
         request falls back to letting the browser post the form. */
      function post(form, btn, done) {
        fetch(addUrl, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        }).then(function (r) {
          return r.json().then(
            function (j) { return { ok: r.ok, body: j }; },
            function ()  { return { ok: r.ok, body: null }; }
          );
        }).then(function (res) {
          if (res.ok) { done(); return; }
          var why = res.body && (res.body.description || res.body.message);
          if (note) { note.textContent = why || OOPS; note.hidden = false; }
          flash(btn, OOPS);
        }).catch(function () {
          form.submit();                                   // offline or blocked: the old way
        });
      }

      all('[data-add]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          if (note) note.hidden = true;
          if (!live) {                                     // demo section, nothing to post
            if (btn.getAttribute('href') === '#') e.preventDefault();
            flash(btn, ADDED);
            return;
          }
          if (!ajax || !window.fetch) return;              // let the form post to the cart
          e.preventDefault();
          post(btn.form || btn.closest('form'), btn, function () { flash(btn, ADDED); ping(); });
        });
      });

      all('[data-checkout]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          if (!live || !window.fetch) return;              // the submit carries return_to
          e.preventDefault();
          if (note) note.hidden = true;
          post(btn.form || btn.closest('form'), btn, function () {
            window.location.href = '/checkout';
          });
        });
      });

      paint();

      /* Line the gallery up with the option that starts selected, without a
         slide: nobody has touched anything yet. */
      if (cur && gal && gal.g900Show) {
        var key0 = cur.getAttribute('data-shot-index');
        if (key0) gal.g900Show(key0, false);
      }

      /* ── sticky bar ──
         It rides on the real buttons, but "the buttons are not in view" is
         also true at the very top of the page — which is how an observer on
         the button alone used to show the bar before anyone had reached the
         section. So it takes both: the section has been reached, and the
         buttons have left the screen. */
      var sticky = box.querySelector('[data-sticky-bar]');
      var acts   = box.querySelector('[data-acts]');
      if (sticky && acts) {
        var queued = false;
        var tick = function () {
          queued = false;
          var h = window.innerHeight || document.documentElement.clientHeight;
          var r = acts.getBoundingClientRect();
          var s = box.getBoundingClientRect();
          var reached = s.top < h * 0.6;
          var away    = r.bottom < 8 || r.top > h - 8;
          sticky.hidden = !(reached && away);
        };
        var soon = function () {
          if (queued) return;
          queued = true;
          requestAnimationFrame(tick);
        };
        window.addEventListener('scroll', soon, { passive: true });
        window.addEventListener('resize', soon, { passive: true });
        tick();
      }
    });
  }

  /* ── sticky anchor nav: [data-g900-nav] ── */
  function nav(root) {
    root.querySelectorAll('[data-g900-nav]').forEach(function (bar) {
      if (bar.dataset.g900Ready) return;
      bar.dataset.g900Ready = '1';
      var fill  = bar.querySelector('[data-progress]');
      var rail  = bar.querySelector('[data-nav-scroll]');
      var links = [].slice.call(bar.querySelectorAll('a[href^="#"]'));
      var FADE  = 36;                                   // px of mask ramp
      var lastOn = null;
      var dragging = false;                             // mouse is panning the rail

      /* Edge fades only appear on the side that still has content, so the
         rail doesn't look clipped when it's scrolled to an end. */
      function fades() {
        if (!rail) return;
        var max = rail.scrollWidth - rail.clientWidth;
        if (max <= 1) {                                 // everything fits
          rail.style.setProperty('--fade-l', '0px');
          rail.style.setProperty('--fade-r', '0px');
          rail.classList.remove('is-grab');
          return;
        }
        var x = rail.scrollLeft;
        rail.classList.add('is-grab');                  // there is room to pan
        rail.style.setProperty('--fade-l', Math.min(FADE, x) + 'px');
        rail.style.setProperty('--fade-r', Math.min(FADE, max - x) + 'px');
      }

      /* Keep the active tab reachable without yanking the page: we set
         scrollLeft on the rail itself rather than calling scrollIntoView,
         which would also scroll every ancestor. Measured from rects, not
         offsetLeft — the rail isn't the offsetParent (the bar is), so
         offsetLeft carries the wrapper padding and mis-centres the tab. */
      function follow(a) {
        if (!rail || !a || a === lastOn || dragging) return;
        lastOn = a;
        var max = rail.scrollWidth - rail.clientWidth;
        if (max <= 1) return;
        var here = a.getBoundingClientRect().left - rail.getBoundingClientRect().left;
        var want = rail.scrollLeft + here - (rail.clientWidth - a.offsetWidth) / 2;
        rail.scrollTo({
          left: Math.max(0, Math.min(max, want)),
          behavior: reduce ? 'auto' : 'smooth'
        });
      }

      if (rail) {
        rail.addEventListener('scroll', fades, { passive: true });
        fades();

        /* Late layout — webfonts swapping in, or the theme editor showing
           the section — changes scrollWidth after init, so re-measure. */
        if ('ResizeObserver' in window) {
          var ro = new ResizeObserver(fades);
          ro.observe(rail);
          if (rail.firstElementChild) ro.observe(rail.firstElementChild);
        }
        window.addEventListener('load', fades);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(fades);

        /* Drag to pan. Touch already swipes natively (and keeps its
           momentum), so we only hijack the mouse; a drag longer than
           DEAD px swallows the click so panning never fires an anchor. */
        var DEAD = 5;
        var moved = 0, startX = 0, startLeft = 0;

        rail.addEventListener('pointerdown', function (e) {
          moved = 0;
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          if (rail.scrollWidth - rail.clientWidth <= 1) return;
          dragging = true;
          startX = e.clientX;
          startLeft = rail.scrollLeft;
          if (rail.setPointerCapture) rail.setPointerCapture(e.pointerId);
        });
        rail.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          var dx = e.clientX - startX;
          if (Math.abs(dx) > DEAD) {
            moved = Math.abs(dx);
            rail.classList.add('is-drag');
          }
          rail.scrollLeft = startLeft - dx;
        });
        function endDrag() {
          if (!dragging) return;
          dragging = false;
          rail.classList.remove('is-drag');
          lastOn = null;                                // let follow() re-centre later
        }
        rail.addEventListener('pointerup', endDrag);
        rail.addEventListener('pointercancel', endDrag);
        window.addEventListener('pointerup', endDrag);   // fallback if capture is unavailable
        rail.addEventListener('click', function (e) {
          if (moved > DEAD) {
            moved = 0;                                   // don't swallow the next real click
            e.preventDefault();
            e.stopPropagation();
          }
        }, true);
      }

      /* Tapping a tab: scroll the target to just under the bar. A native
         anchor jump would park the heading behind the sticky nav, and on
         a rail we also want the tab itself pulled into view. */
      links.forEach(function (a) {
        a.addEventListener('click', function (e) {
          var id = a.getAttribute('href');
          var t  = id && id.length > 1 ? document.querySelector(id) : null;
          if (!t) return;
          e.preventDefault();
          var y = t.getBoundingClientRect().top + window.scrollY - top - bar.offsetHeight - 8;
          window.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
          links.forEach(function (x) { x.classList.toggle('is-on', x === a); });
          lastOn = null;
          follow(a);
          if (window.history && history.replaceState) history.replaceState(null, '', id);
        });
      });

      /* The section wrapper is the sticky element. If a theme ancestor has
         overflow/transform on it, sticky silently does nothing — detect that
         by watching the wrapper ride past its offset, then pin with fixed
         and hold the layout open with a spacer. */
      var host    = bar.closest('.shopify-section') || bar.parentElement;
      var top     = parseFloat(getComputedStyle(bar).getPropertyValue('--top')) || 0;
      var spacer  = null;
      var broken  = false;
      var pinned  = false;

      function pin(resized) {
        if (!broken) {
          if (host === bar || host.getBoundingClientRect().top >= top - 1) return;
          broken = true;                                  // sticky isn't holding
          spacer = document.createElement('div');
          spacer.className = 'g900-subnav__spacer';
          host.insertBefore(spacer, bar);
          host.style.position = 'static';
        }
        var anchor = spacer.getBoundingClientRect().top + window.scrollY;
        var on = window.scrollY + top >= anchor;           // spacer top never moves
        if (on !== pinned) {
          if (on) spacer.style.height = bar.offsetHeight + 'px';   // measure in flow
          pinned = on;
          bar.classList.toggle('is-fixed', on);
          if (!on) spacer.style.height = '0px';
        } else if (on && resized) {
          spacer.style.height = bar.offsetHeight + 'px';
        }
      }

      function tick(resized) {
        pin(resized);
        if (fill) {
          var max = document.documentElement.scrollHeight - window.innerHeight;
          fill.style.width = (max > 0 ? (window.scrollY / max) * 100 : 0) + '%';
        }
        var cur = null;
        links.forEach(function (a) {
          var t = document.querySelector(a.getAttribute('href'));
          if (t && t.getBoundingClientRect().top <= window.innerHeight * 0.38) cur = a;
        });
        links.forEach(function (a) { a.classList.toggle('is-on', a === cur); });
        if (cur) follow(cur);
        if (resized) fades();
      }
      window.addEventListener('scroll', function () { tick(false); }, { passive: true });
      window.addEventListener('resize', function () { tick(true);  }, { passive: true });
      tick(false);
    });
  }

  /* ── lightbox: one node on <body>, shared by every player ──
     Built on first use, torn down on close. The iframe is created
     and destroyed with the modal, so YouTube neither loads on page
     load nor keeps playing behind a closed overlay.              */
  var lb = null, lbMedia = null, lbCap = null, lbFrom = null, lbLoop = null;

  function lbBuild() {
    if (lb) return;
    lb = document.createElement('div');
    lb.className = 'g900-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Video');
    lb.hidden = true;
    lb.innerHTML =
      '<div class="g900-lb__scrim" data-lb-close></div>' +
      '<div class="g900-lb__box">' +
        '<button type="button" class="g900-lb__x" data-lb-close aria-label="Close video"></button>' +
        '<div class="g900-lb__media"></div>' +
        '<p class="g900-lb__cap"></p>' +
      '</div>';
    lbMedia = lb.querySelector('.g900-lb__media');
    lbCap = lb.querySelector('.g900-lb__cap');
    lb.addEventListener('click', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-lb-close')) lbClose();
    });
    document.body.appendChild(lb);
  }

  function lbKeys(e) {
    if (e.key === 'Escape') { lbClose(); return; }
    if (e.key !== 'Tab') return;
    /* Hold focus inside the dialog. Short list on purpose: the close
       button, and whatever the media is — there is nothing else in it. */
    var f = [].slice.call(lb.querySelectorAll('button, iframe, video'));
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* Shape of the frame. w/h — anything taller than it is wide switches
     the modal to portrait, which sizes off viewport height instead of
     width (see .g900-lb.is-portrait). Falsy w/h = the 16:9 default. */
  function lbShape(aw, ah) {
    if (!(aw > 0 && ah > 0)) {
      lb.style.removeProperty('--lb-ar');
      lb.style.removeProperty('--lb-arn');
      lb.classList.remove('is-portrait');
      return;
    }
    lb.style.setProperty('--lb-ar', aw + '/' + ah);
    lb.style.setProperty('--lb-arn', String(aw / ah));
    lb.classList.toggle('is-portrait', aw < ah);
  }

  function lbOpen(box) {
    lbBuild();
    lbFrom = document.activeElement;
    lbMedia.innerHTML = '';

    /* Frame shape, from data-lb-ratio ("w/h"). The real footage gets the
       final say further down, once the file reports its own dimensions. */
    var ar = box.getAttribute('data-lb-ratio');
    if (ar) {
      var parts = ar.split('/');
      lbShape(parseFloat(parts[0]), parseFloat(parts[1]));
    } else {
      /* No setting: read the shape off the card's own well, so a vertical
         clip still opens vertical instead of in a 16:9 letterbox. */
      var well = box.closest ? box.closest('.g900-media') : null;
      var wr = well ? well.getBoundingClientRect() : null;
      if (wr && wr.width > 0 && wr.height > 0) lbShape(wr.width, wr.height);
      else lbShape(0, 0);
    }
    lbCap.textContent = box.getAttribute('data-lb-caption') || '';
    /* portrait mode reserves room under the frame for the caption — no
       caption, no reservation, so the video gets that height instead */
    lb.classList.toggle('has-cap', !!lbCap.textContent);
    lb.setAttribute('aria-label', box.getAttribute('data-title') || 'Video');

    var embed = box.getAttribute('data-embed');
    if (embed) {
      var f = document.createElement('iframe');
      f.src = embed;
      f.title = box.getAttribute('data-title') || 'Product video';
      f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
      f.setAttribute('allowfullscreen', '');
      lbMedia.appendChild(f);
    } else {
      /* Self-hosted: clone the well's <video> so the modal gets the same
         sources with sound and controls, and the one in the page is left
         alone (a muted loop keeps looping behind the scrim, unseen). */
      var src = box.querySelector('video');
      if (!src) {
        /* No footage on this card yet — show its own placeholder at full
           size rather than opening an empty black frame. Only reachable
           from hand-written markup; the player snippet doesn't make a
           clickable player until there is something to play. */
        var ph = box.querySelector('.g900-ph');
        if (!ph) return;
        lbMedia.appendChild(ph.cloneNode(true));
      } else {
        var v = src.cloneNode(true);
        v.className = '';
        v.removeAttribute('muted');
        v.removeAttribute('loop');
        v.muted = false;
        v.loop = false;
        v.controls = true;
        v.setAttribute('controls', '');
        lbMedia.appendChild(v);
        /* The file has the last word on the shape. A 9:16 clip uploaded to a
           card set to 4:5 — or a card with no shape setting at all — would
           otherwise open shrunk inside the wrong frame, which on a phone
           leaves the video too small to see. */
        function shapeFromFile() {
          if (v.videoWidth > 0 && v.videoHeight > 0) lbShape(v.videoWidth, v.videoHeight);
        }
        if (v.readyState >= 1) shapeFromFile();
        v.addEventListener('loadedmetadata', shapeFromFile);
        var p = v.play();
        if (p && p.catch) p.catch(function () { v.muted = true; v.play(); });
      }
    }

    lb.hidden = false;
    document.documentElement.classList.add('g900-lb-open');
    requestAnimationFrame(function () { lb.classList.add('is-open'); });
    var x = lb.querySelector('.g900-lb__x');
    if (x) x.focus();
    document.addEventListener('keydown', lbKeys);

    /* an ambient facade loop would keep decoding behind the scrim */
    lbLoop = box.querySelector('.g900-player__loop');
    if (lbLoop) lbLoop.pause();
  }

  function lbClose() {
    if (!lb || lb.hidden) return;
    lb.classList.remove('is-open');
    document.removeEventListener('keydown', lbKeys);
    document.documentElement.classList.remove('g900-lb-open');
    /* empty AFTER the fade so the frame doesn't blink out from under it */
    function done() { lb.hidden = true; lbMedia.innerHTML = ''; }
    if (reduce) done(); else setTimeout(done, 260);
    if (lbLoop) {
      var q = lbLoop.play();
      if (q && q.catch) q.catch(function () {});
      lbLoop = null;
    }
    if (lbFrom && lbFrom.focus) lbFrom.focus();
    lbFrom = null;
  }

  /* ── click-to-play: [data-g900-player] ──────────────────
     Self-hosted video stays inert until clicked; YouTube/Vimeo
     iframes are only injected on click, so no third-party script
     loads with the page. data-lightbox sends it to the modal
     instead of playing inside the well.                         */
  function player(root) {
    root.querySelectorAll('[data-g900-player]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';

      function start() {
        if (box.getAttribute('data-lightbox')) { lbOpen(box); return; }
        if (box.classList.contains('is-playing')) return;
        box.classList.add('is-playing');

        var embed = box.getAttribute('data-embed');
        if (embed) {
          var f = document.createElement('iframe');
          f.src = embed;
          f.title = box.getAttribute('data-title') || 'Product video';
          f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
          f.setAttribute('allowfullscreen', '');
          f.setAttribute('loading', 'lazy');
          box.appendChild(f);
          return;
        }

        var v = box.querySelector('video');
        if (v) {
          v.removeAttribute('muted');
          v.muted = false;
          v.controls = true;
          var p = v.play();
          // some browsers refuse unmuted autoplay — fall back to muted
          if (p && p.catch) p.catch(function () { v.muted = true; v.play(); });
        }
      }

      box.addEventListener('click', start);
      /* A marquee's duplicated pass ships tabindex="-1" already: it is
         aria-hidden, so it must stay out of the tab order while still
         being clickable. Only untouched players get put in it. */
      if (!box.hasAttribute('tabindex')) box.setAttribute('tabindex', '0');
      box.setAttribute('role', 'button');
      box.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); start(); }
      });
    });
  }

  /* ── carousel: [data-g900-carousel] ─────────────────────────
     One row that drifts on its own and can also be driven by hand.

     Deliberately a REAL scroll container rather than a CSS marquee: a
     transform animation cannot be stepped by a button or nudged by a
     swipe without fighting itself, and native scrolling brings the
     trackpad, touch momentum and the keyboard along for free.

     The track is printed twice. Once the drift has travelled one pass
     we take that distance back off scrollLeft — the cards behind are
     the same cards, so the seam cannot be seen and there is always
     runway in both directions.

     data-car-speed is px per second; 0 leaves the row still but
     swipeable. The drift holds when a mouse rests on it, when a
     keyboard focus is inside, for a beat after any manual scroll,
     off-screen, in a hidden tab, and under prefers-reduced-motion.

     data-car-drag additionally lets a mouse grab the row and pull it. */
  function carousel(root) {
    root.querySelectorAll('[data-g900-carousel]').forEach(function (box) {
      if (box.dataset.g900Ready) return;
      box.dataset.g900Ready = '1';

      var rail  = box.querySelector('[data-car-rail]');
      var track = box.querySelector('[data-car-track]');
      if (!rail || !track) return;
      var prev = box.querySelector('[data-car-prev]');
      var next = box.querySelector('[data-car-next]');
      var FADE = 76;                                    // px of mask ramp

      var speed = parseFloat(box.getAttribute('data-car-speed'));
      if (!(speed >= 0)) speed = 26;                    // 0 is a real answer

      var lap = 0;          // one pass + its gap; 0 when there is no copy to loop
      var max = 0;          // scrollable distance, cached — scrollWidth forces layout
      var pos = 0;          // float mirror of scrollLeft, so <1px per frame accumulates
      var raf = 0, mark = 0, hold = 0, holdT = 0, idleT = 0;
      var hover = false, kbd = false;
      var seen = !('IntersectionObserver' in window);

      function clock() {
        return (window.performance && performance.now) ? performance.now() : Date.now();
      }

      /* Fade only a side that still has cards behind it, so a rail resting
         at an end doesn't look clipped. Mid-drift both sides always have
         more, so both sit at the full ramp — hence the change guard: the
         drift fires a scroll event every frame, and rewriting a custom
         property each time would invalidate style 60 times a second for
         two values that aren't moving. */
      var fadeL = -1, fadeR = -1;
      function fades() {
        var l = 0, r = 0;
        if (max > 1) {
          var x = rail.scrollLeft;
          l = Math.round(Math.min(FADE, x));
          r = Math.round(Math.min(FADE, max - x));
        }
        if (l !== fadeL) { rail.style.setProperty('--fade-l', l + 'px'); fadeL = l; }
        if (r !== fadeR) { rail.style.setProperty('--fade-r', r + 'px'); fadeR = r; }
      }

      function measure() {
        var first = track.firstElementChild;
        /* The copy is a DIRECT child of the track, and only that. A card is
           full of aria-hidden decoration — a star row, a play glyph — and
           a descendant query would happily return one of those and measure
           the lap as 20px. */
        var copy = null;
        for (var i = 0; i < track.children.length; i++) {
          if (track.children[i].getAttribute('aria-hidden') === 'true') { copy = track.children[i]; break; }
        }
        /* Below the desktop breakpoint the copy is display:none — nothing to
           loop, and the rail is an ordinary swipe strip. */
        var loops = !!(first && copy) && getComputedStyle(copy).display !== 'none';
        /* The lap is one pass PLUS the gap after it, measured between a card
           and its copy. scrollWidth / 2 lands half a gap short, and that
           half gap is a visible jump on every single lap. */
        lap = loops
          ? Math.round(copy.getBoundingClientRect().left - first.getBoundingClientRect().left)
          : 0;
        max = rail.scrollWidth - rail.clientWidth;
        /* is-static: not even one pass overflows. The CSS then drops the
           buttons and the fades and centres what is there. */
        box.classList.toggle('is-static', (loops ? lap : track.scrollWidth) <= rail.clientWidth + 8);
        fades();
      }

      /* step by a whole card, so a click never leaves one sliced in half */
      function stepSize() {
        var card = track.firstElementChild;
        var gap  = parseFloat(getComputedStyle(track).columnGap) || 0;
        return card ? Math.round(card.getBoundingClientRect().width + gap)
                    : Math.round(rail.clientWidth * 0.8);
      }

      function live() {
        return speed > 0 && lap > 0 && seen && !hover && !kbd && !reduce &&
               !document.hidden && clock() >= hold &&
               !box.classList.contains('is-static');
      }
      function stop() { if (raf) cancelAnimationFrame(raf); raf = 0; mark = 0; }
      function start() {
        if (!live()) { stop(); return; }
        pos = rail.scrollLeft;
        if (!raf) { mark = 0; raf = requestAnimationFrame(step); }
      }
      function step(ts) {
        raf = 0;
        if (!live()) { mark = 0; return; }
        if (!mark) mark = ts;
        /* clamp the gap so a stalled frame doesn't teleport the row */
        pos += speed * Math.min(ts - mark, 64) / 1000;
        mark = ts;
        if (pos >= lap) pos -= lap;
        rail.scrollLeft = pos;
        raf = requestAnimationFrame(step);
      }

      /* hand the row over for a beat after the visitor touches it */
      function hands(ms) {
        hold = clock() + ms;
        stop();
        clearTimeout(holdT);
        holdT = setTimeout(start, ms + 40);
      }

      function nudge(dir) {
        hands(1600);
        var s = stepSize();
        var x = rail.scrollLeft;
        /* Nothing to the left of the start: hop a lap forward first.
           Invisible, because the cards there are the same cards. */
        if (dir < 0 && lap > 0 && x - s < 0) { x += lap; rail.scrollLeft = x; }
        pos = x;
        if (rail.scrollBy) rail.scrollBy({ left: dir * s, behavior: reduce ? 'auto' : 'smooth' });
        else rail.scrollLeft = x + dir * s;
      }

      if (prev) prev.addEventListener('click', function () { nudge(-1); });
      if (next) next.addEventListener('click', function () { nudge(1); });

      rail.addEventListener('scroll', function () {
        fades();
        /* our own writes land on pos; a bigger delta is the visitor */
        if (Math.abs(rail.scrollLeft - pos) < 2) return;
        hands(900);
        clearTimeout(idleT);
        idleT = setTimeout(function () {
          /* momentum is over — fold back into the first pass so both
             directions still have somewhere to go */
          if (lap > 0 && rail.scrollLeft >= lap) rail.scrollLeft -= lap;
          pos = rail.scrollLeft;
        }, 200);
      }, { passive: true });

      /* the rail is focusable, so arrow keys have to do something sane */
      rail.addEventListener('keydown', function (e) {
        var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        nudge(d);
      });

      /* ── data-car-drag: push the row with the mouse ──────────────
         Touch already has momentum scrolling and a trackpad can scroll
         sideways; a plain mouse has neither, and on a row of video cards
         "grab it and pull" is the gesture people reach for first.

         The threshold is the whole trick: every card in a clip rail is a
         click-to-play player, so the click that ENDS a drag must not also
         open the card it happened to finish on. Below 5px it stays a
         click; past that we take the next click out, once. */
      if (box.hasAttribute('data-car-drag')) {
        var down = false, moved = false, sx = 0, sl = 0, pid = -1, swallowT = 0;

        function swallow(e) { e.stopPropagation(); e.preventDefault(); unswallow(); }
        function unswallow() {
          clearTimeout(swallowT);
          rail.removeEventListener('click', swallow, true);
        }

        rail.classList.add('is-grab');

        rail.addEventListener('pointerdown', function (e) {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          down = true; moved = false;
          sx = e.clientX; sl = rail.scrollLeft; pid = e.pointerId;
        });

        rail.addEventListener('pointermove', function (e) {
          if (!down || e.pointerId !== pid) return;
          var dx = e.clientX - sx;
          if (!moved) {
            if (Math.abs(dx) < 5) return;                 // still a click
            moved = true;
            rail.classList.add('is-drag');
            /* capture, so a pointer that leaves the rail mid-drag keeps
               driving it instead of dropping the gesture on the floor */
            if (rail.setPointerCapture) { try { rail.setPointerCapture(pid); } catch (err) {} }
          }
          e.preventDefault();                             // no text selection, no image drag
          hands(900);                                     // the visitor has the row
          /* A hand that reaches either end hops a whole lap instead of
             stalling against it — the cards a lap away are the same cards,
             so the row feels endless in both directions. */
          var to = sl - dx;
          if (lap > 0) {
            if (to < 0) { sl += lap; to += lap; }
            else if (to > max) { sl -= lap; to -= lap; }
          }
          rail.scrollLeft = to;
          pos = rail.scrollLeft;
        });

        function release() {
          if (!down) return;
          down = false; pid = -1;
          rail.classList.remove('is-drag');
          if (!moved) return;
          moved = false;
          /* fold back into the first pass, exactly as a manual scroll does */
          if (lap > 0 && rail.scrollLeft >= lap) rail.scrollLeft -= lap;
          pos = rail.scrollLeft;
          unswallow();
          rail.addEventListener('click', swallow, true);
          /* a drag that ends outside the rail never fires a click — drop the
             guard on a timer so it can't eat an honest one later */
          swallowT = setTimeout(unswallow, 320);
        }
        rail.addEventListener('pointerup', release);
        rail.addEventListener('pointercancel', release);
        rail.addEventListener('dragstart', function (e) { if (moved) e.preventDefault(); });
      }

      box.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse') { hover = true; stop(); }
      });
      box.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'mouse') { hover = false; start(); }
      });
      /* Keyboard focus only. A tap focuses things too, and treating that as
         "reading" would park the row for good on the first tap. */
      box.addEventListener('focusin', function (e) {
        var vis = false;
        try { vis = !!(e.target && e.target.matches && e.target.matches(':focus-visible')); } catch (err) {}
        if (vis) { kbd = true; stop(); }
      });
      box.addEventListener('focusout', function () { kbd = false; start(); });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            seen = e.isIntersecting;
            if (seen) { measure(); start(); } else stop();
          });
        }, { threshold: 0, rootMargin: '-5% 0px -5% 0px' }).observe(box);
      }

      /* late layout: webfonts swapping in, the breakpoint crossing, or the
         theme editor revealing the section, all change the lap */
      function relayout() { measure(); start(); }
      if ('ResizeObserver' in window) new ResizeObserver(relayout).observe(rail);
      window.addEventListener('resize', relayout, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);

      relayout();
    });
  }

  /* ── boot ──────────────────────────────────────────── */
  function init(root) {
    root = root || document;
    reveal(root); tabs(root); accordion(root); more(root); linked(root); steps(root);
    compare(root); gallery(root); buy(root); nav(root); player(root); carousel(root);
  }

  window.G900 = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // theme editor: re-init just the section that reloaded
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  /* theme editor: a row folded away by more() is clipped out of the
     section, so selecting its block in the sidebar would scroll to
     nothing. Open the fold it belongs to instead. */
  document.addEventListener('shopify:block:select', function (e) {
    var box = e.target && e.target.closest ? e.target.closest('[data-g900-more]') : null;
    if (!box || box.classList.contains('is-open')) return;
    var btn = box.querySelector('[data-more-btn]');
    if (btn) btn.click();
  });
})();
